import type { TFunction } from "i18next";
import Konva from "konva";
import { layoutCanvasPoint, layoutLocalPoint } from "../../lib/schedule-art/layout-coordinates";
import { ART_HEIGHT, ART_WIDTH } from "../../lib/schedule-art/art-canvas";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { getCanvasMetadataSessionId, getCanvasState, getCanvasMetadataTargets } from "../../lib/schedule-art/canvas-state";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { dayBounds, metadataBounds, metadataNodes, type Bounds } from "./metadata-bounds";
import { createMetadataCornerReadout } from "./metadata-corner-readout";
import { metadataTargetLabel } from "./metadata-target-label";
import type { AlignmentGuide } from "./metadata-snapping";

export function attachMetadataEditingOverlay(stage: Konva.Stage, t: TFunction) {
  const labels = metadataLabels(t);
  const layer = new Konva.Layer({ listening: false });
  stage.add(layer);
  layer.getNativeCanvasElement().style.pointerEvents = "none";
  const markings = new Konva.Group({ listening: false });
  layer.add(markings);
  const readout = createMetadataCornerReadout(stage, layer);
  let hovered: string | null = null;
  let inspected: string | null = null;
  let dragging: Konva.Node | null = null;
  let targetBindings = new Map<string, string>();
  let sessionId = "";

  const findNode = (path: string) =>
    stage.findOne((node: Konva.Node) => node.getAttr("metadataPath") === path && node.getClientRect().width > 0);
  const pathFromNode = (node: Konva.Node): string | null => {
    const own = node.getAttr("metadataPath") as string | undefined;
    if (own) return own;
    const parent = node.getParent();
    return parent ? pathFromNode(parent) : null;
  };
  const draw = () => {
    markings.destroyChildren();
    readout.hide();
    const state = getCanvasState();
    if (!state.developerMode) return;
    const view = useMetadataEditingStore.getState();
    const scale = stage.scaleX() || 1;
    const targets = getCanvasMetadataTargets();
    const outline = (bounds: Bounds, color: string, dashed = false) => {
      markings.add(
        new Konva.Rect({
          ...bounds,
          stroke: color,
          strokeWidth: 1.5 / scale,
          dash: dashed ? [5 / scale, 4 / scale] : undefined,
        }),
      );
    };
    const paths = new Set([hovered, view.selected].filter((path): path is string => !!path));
    const days = new Set<string>();
    const avoidedBounds: Bounds[] = [];
    for (const path of paths) {
      const target = targets.find((target) => target.path === path);
      if (!target) continue;
      const node = findNode(path);
      if (target.day) days.add(target.day);
      if (node) {
        const bounds = metadataBounds(node);
        avoidedBounds.push(bounds);
        outline(bounds, path === view.selected ? "#0284c7" : "#f59e0b");
      }
    }
    if (view.dayBounds) {
      for (const day of days) {
        const bounds = dayBounds(stage, day);
        if (bounds) outline(bounds, "#7c3aed", true);
      }
    }
    const activePath = dragging ? pathFromNode(dragging) : (hovered ?? inspected ?? view.selected);
    const target = targets.find((target) => target.path === activePath);
    if (target) {
      const node = findNode(target.path);
      const origin = target.origin;
      const point = node ? layoutLocalPoint([node.x(), node.y()], origin, target.layoutRotation) : target.point;
      const canvasPoint = layoutCanvasPoint(point, origin, target.layoutRotation);
      const bounds = node
        ? metadataBounds(node)
        : { x: canvasPoint[0]!, y: canvasPoint[1]!, width: 0, height: 0 };
      const offscreen =
        bounds.x + bounds.width < 0 || bounds.y + bounds.height < 0 || bounds.x > ART_WIDTH || bounds.y > ART_HEIGHT;
      const padding = 8 / scale;
      const clamp = (value: number, max: number) => Math.max(padding, Math.min(value, max - padding));
      if (offscreen || !node) {
        const x = clamp(bounds.x, ART_WIDTH);
        const y = clamp(bounds.y, ART_HEIGHT);
        const radius = 5 / scale;
        markings.add(new Konva.Circle({ x, y, radius, fill: "#0284c7", stroke: "white", strokeWidth: 1 / scale }));
        avoidedBounds.push({ x: x - radius, y: y - radius, width: radius * 2, height: radius * 2 });
      }
      const name = metadataTargetLabel(target, labels);
      const parts = target.path.split(".");
      const section = target.day ? labels[target.day] : labels.globals;
      const variant = target.day ? labels[parts[2]!] : "";
      const index = Number(parts.at(-1));
      const suffix = Number.isInteger(index) ? ` #${index + 1}` : "";
      const status = offscreen ? t("metadata.canvas.outside") : !node ? t("metadata.canvas.notRendered") : "";
      const detail =
        status || [...new Set([section, variant].filter((value) => value && value !== name))].join(" · ") + suffix;
      if (!node) avoidedBounds.push(bounds);
      readout.update(name, detail, point, avoidedBounds, !!status);
    }
    const guides = dragging?.getAttr("metadataGuides") as AlignmentGuide[] | undefined;
    guides?.forEach((guide) =>
      markings.add(
        new Konva.Line({
          points:
            guide.axis === "x" ? [guide.value, 0, guide.value, ART_HEIGHT] : [0, guide.value, ART_WIDTH, guide.value],
          stroke: "#e11d48",
          strokeWidth: 1 / scale,
          dash: [4 / scale, 3 / scale],
        }),
      ),
    );
  };
  const refresh = () => {
    const state = getCanvasState();
    const view = useMetadataEditingStore.getState();
    const paths = metadataNodes(stage)
      .filter((node) => node.getClientRect().width > 0)
      .map((node) => node.getAttr("metadataPath") as string);
    if (paths.join("|") !== view.renderedPaths.join("|")) useMetadataEditingStore.setState({ renderedPaths: paths });
    const targets = getCanvasMetadataTargets();
    const bindings = new Map(targets.map(target => [target.path, target.bind]));
    const activeSessionId = getCanvasMetadataSessionId();
    const sameSession = activeSessionId === sessionId;
    const previousBinding = view.selected ? targetBindings.get(view.selected) : undefined;
    const selectedBinding = view.selected ? bindings.get(view.selected) : undefined;
    const bindingChanged = sameSession && previousBinding !== undefined && previousBinding !== selectedBinding;
    const hiddenAnchor = !state.anchorsVisible && view.selected?.endsWith(".origin");
    if (view.selected && (hiddenAnchor || bindingChanged || !bindings.has(view.selected)))
      view.select(null);
    targetBindings = bindings;
    sessionId = activeSessionId;
    hovered = null;
    dragging = null;
    useMetadataEditingStore.setState({ livePoint: null });
    draw();
  };
  stage.on("mouseover.metadataEditing", (event) => {
    hovered = pathFromNode(event.target);
    if (hovered) inspected = hovered;
    draw();
  });
  stage.on("mouseout.metadataEditing", () => {
    hovered = null;
    draw();
  });
  stage.on("mouseleave.metadataEditing", () => {
    inspected = null;
    draw();
  });
  stage.on("pointerdown.metadataEditing", (event) => {
    if (!getCanvasState().developerMode) return;
    useMetadataEditingStore.getState().select(pathFromNode(event.target));
  });
  stage.on("dragstart.metadataEditing", (event) => {
    if (!getCanvasState().developerMode) return;
    const path = pathFromNode(event.target);
    if (!path) return;
    dragging = event.target;
    useMetadataEditingStore.getState().select(path);
    draw();
  });
  stage.on("dragmove.metadataEditing", (event) => {
    if (!dragging || event.target !== dragging) return;
    const origin = dragging.getAttr("metadataOrigin") as number[];
    const rotation = dragging.getAttr("metadataLayoutRotation") as number | undefined;
    useMetadataEditingStore.setState({
      livePoint: layoutLocalPoint([dragging.x(), dragging.y()], origin, rotation).map(Math.round),
    });
    draw();
  });
  stage.on("dragend.metadataEditing", () => {
    dragging = null;
    useMetadataEditingStore.setState({ livePoint: null });
    draw();
  });
  const unsubscribe = useMetadataEditingStore.subscribe((state, previous) => {
    const selectionChanged = state.selected !== previous.selected;
    if (selectionChanged) inspected = null;
    if (selectionChanged || state.dayBounds !== previous.dayBounds) draw();
  });
  return {
    refresh,
    resize: draw,
    dispose: () => {
      unsubscribe();
      stage.off(".metadataEditing");
      readout.dispose();
      layer.destroy();
    },
  };
}
